import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TripleZoneBodyDirective } from '../../shared/components/layouts/triple-zone/triple-zone-body.directive';
import { TripleZoneFooterDirective } from '../../shared/components/layouts/triple-zone/triple-zone-footer.directive';
import { TripleZoneHeaderDirective } from '../../shared/components/layouts/triple-zone/triple-zone-header.directive';
import { TripleZoneComponent } from '../../shared/components/layouts/triple-zone/triple-zone.component';
import { ButtonComponent } from '../../shared/components/ui/button/button.component';
import { TranslateModule } from '@ngx-translate/core';
import { NgClass, NgIf, NgOptimizedImage } from '@angular/common';
import { LogoComponent } from '../../shared/components/ui/logo/logo.component';
import { CartRestService } from '../../core/rest/cart/cart-rest.service';
import { catchError, EMPTY, Subscription, switchMap, take, takeWhile, timer } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { IconComponent } from '../../shared/components/ui/icon/icon.component';
import { ProgressSpinnerComponent } from '../../shared/components/ui/progress-spinner/progress-spinner.component';
import { PaymentProcessResponse, PaymentStatus, PaymentStatusResponse } from '../../core/rest/cart/model/wspay.model';

@Component({
  selector: 'app-payment-confirmation',
  standalone: true,
  imports: [
    RouterLink,
    ButtonComponent,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    TripleZoneBodyDirective,
    TripleZoneComponent,
    TripleZoneFooterDirective,
    TripleZoneHeaderDirective,
    TranslateModule,
    NgOptimizedImage,
    LogoComponent,
    NgIf,
    IconComponent,
    NgClass,
    ProgressSpinnerComponent,
  ],
  templateUrl: './payment-confirmation.component.html',
  styleUrl: './payment-confirmation.component.scss',
})
export class PaymentConfirmationComponent implements OnInit, OnDestroy {
  /**
   * Four polls three seconds apart gave up after nine seconds, and an ordinary
   * successful purchase needs more than that: the BAPI health check, issuing the
   * fiscal document and the WSPay capture together took twelve seconds on a
   * measured run. The buyer was shown "this is taking longer than usual" for a
   * sale that had in fact gone through, which reads like a failure and invites
   * them to pay again.
   *
   * Forty-five seconds covers the normal case with room for a slow BAPI, and is
   * still short enough that nobody is left staring. Past it the message is
   * honest: the queue really is still working, and the answer will arrive by
   * e-mail.
   */
  private static readonly MAX_STATUS_POLLS = 15;
  private readonly route = inject(ActivatedRoute);
  private statusSubscription?: Subscription;

  successResponse = '0';
  paymentStatus: PaymentStatus = 'processing';
  failureAfterAuthorization = false;
  downloadLink?: string;

  constructor(private cartRestService: CartRestService) {
  }

  ngOnInit(): void {
    const params = this.getParamsFromUrl();
    this.successResponse = params.success;
    // WSPay can first return a signed provisional envelope with Success=0
    // and no STAN/ApprovalCode. It is not a declined card. Always let the
    // API validate it and start the durable StatusCheck instead of showing a
    // false failure in the buyer's browser.
    if (!params.shoppingCartId || !params.signature) {
      this.paymentStatus = 'failed';
      return;
    }

    this.cartRestService.sendPaymentProcessResponse({
      successResponse: Number(params.success),
      shoppingCartId: params.shoppingCartId,
      dateTime: params.dateTime,
      approvalCode: params.approvalCode,
      signature: params.signature,
      errorMessage: params.errorMessage,
      amount: params.amount,
      stan: params.stan,
      wsPayOrderId: params.wsPayOrderId,
    })
      .pipe(
        catchError((_error: HttpErrorResponse) => {
          this.failureAfterAuthorization = this.successResponse === '1';
          this.paymentStatus = 'failed';
          return EMPTY;
        })
      )
      .subscribe((response: PaymentProcessResponse) => {
        if (response.status === 'failed' || response.status === 'voided') {
          this.failureAfterAuthorization = response.status === 'failed' && this.successResponse === '1';
          this.paymentStatus = response.status;
          return;
        }

        const token = response?.statusToken ?? params.paymentToken ?? sessionStorage.getItem(`wspay-status-token:${params.shoppingCartId}`);
        if (!token) {
          this.failureAfterAuthorization = true;
          this.paymentStatus = 'failed';
          return;
        }
        this.startStatusPolling(token);
      });
  }

  ngOnDestroy(): void {
    this.statusSubscription?.unsubscribe();
  }

  getParamsFromUrl(): {
    shoppingCartId: string,
    dateTime: string,
    approvalCode: string,
    signature: string,
    errorMessage: string,
    amount: string,
    stan: string,
    wsPayOrderId: string,
    success: string,
    paymentToken: string
  } {
    const queryParams = this.route.snapshot.queryParams;
    return {
      shoppingCartId: queryParams['ShoppingCartID'],
      dateTime: queryParams['DateTime'],
      stan: queryParams['STAN'],
      wsPayOrderId: queryParams['WsPayOrderId'],
      success: queryParams['Success'],
      approvalCode: queryParams['ApprovalCode'],
      signature: queryParams['Signature'],
      errorMessage: queryParams['ErrorMessage'],
      amount: queryParams['Amount'],
      paymentToken: queryParams['paymentToken'],
    };
  }

  downloadTicket(): void {
    if (this.downloadLink) {
      window.open(this.downloadLink, '_blank');
    }
  }

  private startStatusPolling(token: string): void {
    this.statusSubscription = timer(0, 3000).pipe(
      switchMap(() => this.cartRestService.getPaymentStatus(token)),
      // A durable queue continues in the background. Do not make a buyer
      // stare at a spinner forever when a dependent service is unavailable.
      take(PaymentConfirmationComponent.MAX_STATUS_POLLS),
      takeWhile((response: PaymentStatusResponse) => response.status === 'processing', true),
      catchError(() => {
        this.failureAfterAuthorization = true;
        this.paymentStatus = 'failed';
        return EMPTY;
      })
    ).subscribe((response: PaymentStatusResponse) => {
      this.paymentStatus = response.status;
      if (response.status === 'failed') {
        this.failureAfterAuthorization = true;
      }
      this.downloadLink = response.downloadLink;
    }, undefined, () => {
      if (this.paymentStatus === 'processing') {
        this.paymentStatus = 'pending';
      }
    });
  }
}
